import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'

export type DimensionStatus = 'PASS' | 'WARNING' | 'FAIL' | 'SKIPPED'
export type ReviewStatus = 'PENDING' | 'COMPLETED' | 'FAILED'

export interface ReviewDimension {
  name: string
  label: string
  status: DimensionStatus
  rationale: string
}

export interface DesignReview {
  id: string
  designId: string
  status: ReviewStatus
  dimensions?: ReviewDimension[]
  issueCount?: number
  createdAt: string
  updatedAt: string
}

const REVIEW_QUERY_KEY = 'design-review'

export const useDesignReview = (designId: string | null) => {
  return useQuery({
    queryKey: [REVIEW_QUERY_KEY, designId],
    queryFn: () =>
      axios
        .get<DesignReview>(`/designs/${designId}/review`)
        .then(({ data }) => data),
    enabled: !!designId,
    refetchInterval: (query) => {
      if (query.state.data?.status === 'PENDING') return 2000
      return false
    },
    retry: (failureCount, error) => {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return false
      }
      return failureCount < 2
    }
  })
}

export const useTriggerDesignReview = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      designId,
      brandAnalysis
    }: {
      designId: string
      brandAnalysis?: Record<string, unknown> | null
    }) =>
      axios
        .post<DesignReview>(`/designs/${designId}/review`, {
          brandAnalysis: brandAnalysis ?? undefined
        })
        .then(({ data }) => data),
    onSuccess: (data) => {
      queryClient.setQueryData([REVIEW_QUERY_KEY, data.designId], data)
    }
  })
}
